import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getToken, setToken, ApiError, Unauthorized } from './api';
import { Icon } from './ui';
import { Kyc } from './screens/Kyc';
import { Triage } from './screens/Triage';
import { Drivers } from './screens/Drivers';
import { Trips } from './screens/Trips';
import { Supply } from './screens/Supply';
import { Audit } from './screens/Audit';
import { Settings } from './screens/Settings';
import { Support } from './screens/Support';
import { Checks } from './screens/Checks';
import { Riders } from './screens/Riders';
import { Chats } from './screens/Chats';
import { Accounts } from './screens/Accounts';
import { People } from './screens/People';

type Tab = 'triage' | 'kyc' | 'drivers' | 'riders' | 'trips' | 'chats' | 'supply' | 'audit' | 'settings'
  | 'support' | 'people' | 'checks' | 'accounts';

/**
 * support.chalotransit.io is the support dashboard: the same app, branded for
 * agents and showing only the desk and the people lookup — to admins too, who
 * have the full console at the main address.
 */
const SUPPORT_HOST = window.location.hostname.startsWith('support.');

/** Who may use the console. Support agents see only the support desk. */
const CONSOLE_ROLES = ['admin', 'support'];

interface Me { id: string; role: string; fullName: string | null }

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState<Tab>('triage');
  const [counts, setCounts] = useState({ sos: 0, kyc: 0, help: 0, callbacks: 0, running: 0 });
  const [budget, setBudget] = useState<{ day: number; cap: number } | null>(null);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try { return localStorage.getItem('chalo.console.theme') === 'light' ? 'light' : 'dark'; } catch { return 'dark'; }
  });
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem('chalo.console.theme', theme); } catch { /* private window */ }
  }, [theme]);
  const [focusTicket, setFocusTicket] = useState<string | null>(null);

  const signOut = useCallback(() => {
    setToken(null);
    setMe(null);
  }, []);

  // Resume a session on reload. A token that is no longer staff's — the
  // role was changed, or it expired — must drop straight back to the login
  // rather than leaving a shell that 403s on every panel.
  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    api.me()
      .then((u) => {
        if (!CONSOLE_ROLES.includes(u.role)) {
          signOut();
          return;
        }
        setMe(u);
        if (u.role === 'support' || SUPPORT_HOST) setTab('support');
      })
      .catch(() => signOut())
      .finally(() => setChecking(false));
  }, [signOut]);

  // The counts that belong in the chrome, because they are the reasons to
  // leave whatever screen you are on. The overview is an admin's; an agent
  // only needs to know who is waiting.
  useEffect(() => {
    if (!me) return;
    const onErr = (e: unknown) => { if (e instanceof Unauthorized) signOut(); };
    const tick = () => {
      if (me.role === 'admin' && !SUPPORT_HOST) {
        api.overview()
          .then((o) => setCounts((c) => ({ ...c, sos: o.sosOpen, kyc: o.kycPending, running: o.tripsRunning })))
          .catch(onErr);
        api.budget()
          .then((b) => setBudget({ day: b.day_paise, cap: b.daily_cap_paise }))
          .catch(() => undefined);
      }
      api.supportQueue('active')
        .then((q) => setCounts((c) => ({ ...c, help: q.filter((t) => t.awaitingReply).length, callbacks: q.filter((t) => t.callbackRequested).length })))
        .catch(onErr);
    };
    void tick();
    const t = setInterval(tick, 15_000);
    return () => clearInterval(t);
  }, [me, signOut]);

  if (checking) {
    return <div className="login-wrap" />;
  }

  if (!me) {
    return <Login onIn={(u) => { setMe(u); setTab(u.role === 'support' || SUPPORT_HOST ? 'support' : 'triage'); }} />;
  }

  const isAdmin = me.role === 'admin';
  const helpTab = { id: 'support' as Tab, label: 'Support desk', icon: 'support_agent', count: counts.help || undefined, tone: 'todo' as const };
  const peopleTab = { id: 'people' as Tab, label: 'Find rider or driver', icon: 'person_search' };
  const nav: { id: Tab; label: string; icon: string; count?: number; tone?: 'urgent' | 'todo' }[] =
    !isAdmin || SUPPORT_HOST ? [helpTab, peopleTab] : [
      { id: 'triage', label: 'Triage', icon: 'emergency', count: counts.sos || undefined, tone: 'urgent' },
      { id: 'kyc', label: 'Document review', icon: 'fact_check', count: counts.kyc || undefined, tone: 'todo' },
      { id: 'drivers', label: 'Drivers', icon: 'badge' },
      { id: 'riders', label: 'Riders', icon: 'groups' },
      { id: 'trips', label: 'Trips & live fleet', icon: 'directions_car' },
      { id: 'chats', label: 'Chats', icon: 'forum' },
      helpTab,
      peopleTab,
      { id: 'supply', label: 'Supply gaps', icon: 'grid_view' },
      { id: 'checks', label: 'Route & maps checks', icon: 'troubleshoot' },
      { id: 'audit', label: 'Audit log', icon: 'receipt_long' },
      { id: 'settings', label: 'Settings', icon: 'settings' },
      { id: 'accounts', label: 'Clean up accounts', icon: 'delete_sweep' },
    ];
  const initials = (me.fullName ?? (isAdmin ? 'Admin' : 'Support')).split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const openConversation = (ticketId: string) => {
    setFocusTicket(ticketId);
    setTab('support');
  };

  return (
    <div className="shell">
      <nav className="rail">
        <div className="brand">
          <span className="brand-plate"><i>IND</i>{SUPPORT_HOST || !isAdmin ? 'CHALO SUPPORT' : 'CHALO OPS'}</span>
        </div>
        <div className="rail-label">{isAdmin && !SUPPORT_HOST ? 'OPERATIONS DESK' : 'SUPPORT DESK'}</div>
        <div className="nav">
          {nav.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              aria-current={tab === n.id ? 'page' : undefined}
            >
              <Icon name={n.icon} fill={tab === n.id} />
              <span className="nav-label">{n.label}</span>
              {n.count ? <span className={`count ${n.tone ?? ''}`}>{n.count}</span> : null}
            </button>
          ))}
        </div>
        <div className="rail-foot">
          <div className="hub"><span className="dot" /> Noida · live</div>
          <button onClick={signOut}><Icon name="logout" size={16} /> Sign out</button>
        </div>
      </nav>

      <div className="workspace">
        <header className="topbar">
          <div className="netpill">
            <span className={`dot ${counts.sos ? 'bad' : ''}`} />
            {isAdmin && !SUPPORT_HOST
              ? <>{counts.running} running {counts.running === 1 ? 'trip' : 'trips'}{counts.sos ? <> · <b>{counts.sos} SOS open</b></> : ' · all normal'}</>
              : <>{counts.help} waiting for a reply{counts.callbacks ? <> · <b>{counts.callbacks} call-back{counts.callbacks === 1 ? '' : 's'}</b></> : ''}</>}
          </div>
          <div className="topchip"><Icon name="calendar_today" size={17} /> {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
          <div className="spacer" />
          {isAdmin && !SUPPORT_HOST && budget ? (
            <div className="quota" title="Google Maps spend today against the daily cap">
              <span>Maps today</span>
              <b>₹{Math.round(budget.day / 100)} <small>/ ₹{Math.round(budget.cap / 100)}</small></b>
              <i><em style={{ width: `${Math.min(100, (budget.day / Math.max(1, budget.cap)) * 100)}%` }} /></i>
            </div>
          ) : null}
          <button className="iconbtn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} title={theme === 'dark' ? 'Light theme' : 'Dark theme'} aria-label="Switch theme">
            <Icon name={theme === 'dark' ? 'light_mode' : 'dark_mode'} />
          </button>
          <div className="who">
            <div><b>{me.fullName ?? (isAdmin ? 'Admin' : 'Support')}</b><small>{isAdmin ? 'Administrator' : 'Support agent'}</small></div>
            <span className="avatar-sm">{initials}</span>
          </div>
        </header>

      <main className="main">
        {tab === 'triage' ? <Triage onGo={(t) => setTab(t as Tab)} /> : null}
        {tab === 'kyc' ? <Kyc /> : null}
        {tab === 'drivers' ? <Drivers /> : null}
        {tab === 'trips' ? <Trips /> : null}
        {tab === 'supply' ? <Supply /> : null}
        {tab === 'audit' ? <Audit /> : null}
        {tab === 'settings' ? <Settings /> : null}
        {tab === 'support' ? <Support isAdmin={isAdmin && !SUPPORT_HOST} focus={focusTicket} /> : null}
        {tab === 'people' ? <People onConversation={openConversation} /> : null}
        {tab === 'riders' ? <Riders /> : null}
        {tab === 'chats' ? <Chats /> : null}
        {tab === 'accounts' ? <Accounts /> : null}
        {tab === 'checks' ? <Checks /> : null}
      </main>
      </div>
    </div>
  );
}

function Login({ onIn }: { onIn: (m: Me) => void }) {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState<{ googleClientId: string | null; adminsByGoogleOnly: boolean } | null>(null);
  const gbtn = useRef<HTMLDivElement>(null);

  const admit = (r: { accessToken: string; user: Me }) => {
    // The role is checked here rather than after letting them in, so a
    // rider or driver never sees the shell at all.
    if (!CONSOLE_ROLES.includes(r.user.role)) {
      setErr(cfg?.adminsByGoogleOnly
        ? 'That account is not an administrator or support agent. Admins sign in with their Google account.'
        : 'That account is not an administrator or support agent.');
      return;
    }
    setToken(r.accessToken);
    onIn(r.user);
  };

  // Google sign-in, for admins: the button is Google's own, and the ID token
  // it returns is checked by the server against the allowed admin emails.
  useEffect(() => {
    api.consoleConfig().then(setCfg).catch(() => setCfg({ googleClientId: null, adminsByGoogleOnly: false }));
  }, []);
  useEffect(() => {
    if (!cfg?.googleClientId || !gbtn.current) return;
    const render = () => {
      const g = (window as unknown as { google?: GoogleId }).google;
      if (!g || !gbtn.current) return;
      g.accounts.id.initialize({
        client_id: cfg.googleClientId!,
        callback: async ({ credential }) => {
          setBusy(true);
          setErr(null);
          try {
            admit(await api.googleLogin(credential));
          } catch (e) {
            setErr(e instanceof ApiError ? e.message : 'Google sign-in failed');
          } finally {
            setBusy(false);
          }
        },
      });
      g.accounts.id.renderButton(gbtn.current, { theme: 'outline', size: 'large', text: 'signin_with', width: 300 });
    };
    if ((window as unknown as { google?: GoogleId }).google) return render();
    const tag = document.createElement('script');
    tag.src = 'https://accounts.google.com/gsi/client';
    tag.async = true;
    tag.onload = render;
    document.head.appendChild(tag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.requestOtp(phone);
      setDevCode(r.devCode ?? null);
      setStep('code');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send a code');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setErr(null);
    try {
      admit(await api.verifyOtp(phone, code));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form
        className="login"
        onSubmit={(e) => {
          e.preventDefault();
          void (step === 'phone' ? send() : verify());
        }}
      >
        <div className="brand">
          <span className="brand-plate"><i>IND</i>{SUPPORT_HOST ? 'CHALO SUPPORT' : 'CHALO OPS'}</span>
        </div>
        <h1>{step === 'phone' ? 'Sign in' : 'Enter the code'}</h1>

        {err ? <div className="err">{err}</div> : null}

        {step === 'phone' && cfg?.googleClientId ? (
          <>
            <p>{SUPPORT_HOST ? 'Admins: sign in with Google.' : 'Administrators: sign in with your Google account.'}</p>
            <div ref={gbtn} className="gbtn" />
            <div className="divider">support agents</div>
          </>
        ) : null}

        <p>
          {step === 'phone'
            ? 'Support agents sign in with their phone number. You will get a six-digit code by SMS.'
            : `Sent to +91 ${phone}`}
        </p>
        {devCode ? <div className="dev">Development build — your code is {devCode}</div> : null}

        {step === 'phone' ? (
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="98765 43210"
            autoComplete="tel"
            autoFocus
          />
        ) : (
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="------"
            inputMode="numeric"
            autoComplete="one-time-code"
            style={{ letterSpacing: 8, textAlign: 'center', fontFamily: 'var(--mono)' }}
            autoFocus
          />
        )}

        <div className="row">
          {step === 'code' ? (
            <button
              type="button"
              className="btn"
              onClick={() => { setStep('phone'); setCode(''); setDevCode(null); setErr(null); }}
            >
              Back
            </button>
          ) : null}
          <button
            type="submit"
            className="btn primary"
            disabled={busy || (step === 'phone' ? phone.length < 10 : code.length < 6)}
          >
            {busy ? 'Working…' : step === 'phone' ? 'Send code' : 'Sign in'}
          </button>
        </div>
      </form>
    </div>
  );
}

/** The slice of Google Identity Services the console uses. */
interface GoogleId {
  accounts: {
    id: {
      initialize(o: { client_id: string; callback: (r: { credential: string }) => void }): void;
      renderButton(el: HTMLElement, o: Record<string, unknown>): void;
    };
  };
}
